import { IconProps } from "@tabler/icons-react";
import classNames from "classnames";
import Link, { LinkProps } from "next/link";
import { useRouter } from "next/router";
import React, { type CSSProperties, ReactNode, useMemo } from "react";

import { Flex, Anchor as RootAnchor, AnchorProps as RootAnchorProps, Text, tokens, useHover } from "~/ui";

type AnchorFontSize = keyof typeof tokens.typography.fontSize;
type AnchorFontWeight = keyof typeof tokens.typography.fontWeight | number;
type AnchorColor = "gray" | "black";

export type AnchorProps = Omit<RootAnchorProps, "color" | "children" | "href" | "fz" | "fw" | "ref"> & {
  readonly children: string | ReactNode;
  readonly href: LinkProps["href"];
  readonly fz?: AnchorFontSize;
  readonly fw?: AnchorFontWeight;
  readonly color?: AnchorColor;
  readonly active?: boolean | RegExp | ((pathname: string) => boolean);
  readonly iconLeft?: React.ComponentType<IconProps>;
  readonly iconRight?: React.ComponentType<IconProps>;
};

const anchorIsActive = (a: AnchorProps["active"], pathname: string): boolean => {
  if (a !== undefined) {
    return typeof a === "function" ? a(pathname) : a instanceof RegExp ? a.test(pathname) : a;
  }
  return false;
};

export const Anchor = ({
  children,
  href,
  iconLeft: IconLeft,
  iconRight: IconRight,
  active,
  color,
  fz = "sm",
  fw = tokens.typography.fontWeight.regular,
  ...props
}: AnchorProps): JSX.Element => {
  const { hovered, ref } = useHover<HTMLAnchorElement>();
  const { pathname } = useRouter();

  const _active = useMemo(() => anchorIsActive(active, pathname), [active, pathname]);

  // The removed `sx` also carried `&:hover,&:focus,&.active { text-decoration: none }`. That is now
  // `underline={false}` below rather than a CSS Module: Mantine's own hover rule and a module class
  // have the same specificity (0,2,0), so which one won would depend on stylesheet order — and
  // Mantine's emotion cache is `prepend: true` on the client but emitted after the CSS links during
  // SSR, so the same link would resolve differently server- and client-side. `underline={false}`
  // suppresses Mantine's rule at source instead of racing it.
  const style = useMemo<CSSProperties>(() => {
    const baseColor = color ?? tokens.color.content.secondary;
    const fontWeight = typeof fw === "string" ? tokens.typography.fontWeight[fw] : fw;
    const base: CSSProperties = {
      fontWeight,
      lineHeight: tokens.typography.textLineHeight[fz],
      color: baseColor,
      fontSize: tokens.typography.fontSize[fz],
      textDecoration: "none",
      maxWidth: "fit-content",
    };
    if (_active || hovered) {
      return { ...base, color: color ? tokens.color.black : tokens.color.content.secondaryHover };
    }
    return base;
  }, [color, hovered, _active, fz, fw]);

  // `style` is merged with the caller's, not substituted for it. On the base branch this component
  // set `sx`, so a caller's `style` passed through `{...props}` and landed inline, beating the
  // emotion class — `ChainNode.tsx:213` relies on that for `color: inherit` and `display: block`.
  // Setting `style` alone after the spread would silently discard it.
  return (
    <RootAnchor
      {...props}
      ref={ref}
      component={Link}
      underline={false}
      title={typeof children === "string" ? children : ""}
      className={classNames({ active: _active }, props.className)}
      href={href}
      style={{ ...style, ...props.style }}
    >
      <Flex direction="row" align="center">
        {IconLeft && <IconLeft style={{ padding: 2, marginRight: 4 }} size={tokens.typography.textLineHeight[fz]} />}
        <Text
          style={{
            fontSize: tokens.typography.fontSize[fz],
            lineHeight: tokens.typography.textLineHeight[fz],
          }}
        >
          {children}
        </Text>
        {IconRight && <IconRight style={{ padding: 2, marginLeft: 4 }} size={tokens.typography.textLineHeight[fz]} />}
      </Flex>
    </RootAnchor>
  );
};
